<template>
    <div class="onboarding-quicktrial__signup">
        <header>
            <p
                :class="[
                    'onboarding-quicktrial__signup-title',
                    isDesktop ? 'typographies-display-medium' : 'typographies-display-small',
                ]"
            >
                {{ $t.__('pages.onboarding.trial.OnboardingQuicktrial.title') }}
            </p>
        </header>
        <div>
            <!-- Sign with Google -->
            <CompassButton
                :label="$t.__('common.actions.subscribe_with_google')"
                type="secondary"
                class="onboarding-quicktrial__signup-google-button"
                :size="componentSize"
                @click="signUpGoogle"
            >
                <template #prefix>
                    <SaasIcon icon="svgGoogle" :width="iconSize" :height="iconSize" />
                </template>
            </CompassButton>

            <p class="onboarding-quicktrial__signup-divider">
                <span
                    class="onboarding-quicktrial__signup-divider-text typographies-paragraph-medium-semibold"
                >
                    {{ $t.__('common.labels.or') }}
                </span>
            </p>

            <!-- Sign with Account -->
            <el-form
                ref="form"
                :rules="rules"
                inlineMessage
                labelPosition="top"
                :model="form"
                class="onboarding-quicktrial__signup-form"
            >
                <!-- Company Name -->
                <el-form-item
                    :label="$t.__('common.labels.your_company_name')"
                    prop="name"
                    :error="errors.get('name')"
                >
                    <CompassInput v-model="form.name" :size="componentSize" data-bot="quicktrial__companyname" />
                </el-form-item>

                <CompassFormItem
                    :label="$t.__('common.labels.country')"
                    :size="isDesktop ? 'large' : 'medium'"
                    tooltip="Sélectionnez le pays dans lequel votre établissement est immatriculé."
                >
                    <CompassSelect
                        v-model="form.country"
                        :filterable="true"
                        :size="isDesktop ? 'large' : 'medium'"
                    >
                        <CompassDropdownOption
                            v-for="item in countries"
                            :key="item.code"
                            :label="item.name"
                            :value="item.code"
                            :flag="item.code.toLowerCase()"
                            type="flag"
                        />
                    </CompassSelect>
                </CompassFormItem>

                <div class="onboarding-quicktrial__signup-form-items-horizontal">
                    <!-- Last Name -->
                    <el-form-item
                        class="onboarding-quicktrial__signup-form-item"
                        :label="$t.__('common.labels.name')"
                        prop="lastname"
                        :error="errors.get('lastname')"
                    >
                        <CompassInput v-model="form.lastname" :size="componentSize" data-bot="quicktrial__lastname" />
                    </el-form-item>

                    <!-- First Name -->
                    <el-form-item
                        class="onboarding-quicktrial__signup-form-item"
                        :label="$t.__('common.labels.first_name')"
                        prop="firstname"
                        :error="errors.get('firstname')"
                    >
                        <CompassInput v-model="form.firstname" :size="componentSize" data-bot="quicktrial__firstname"/>
                    </el-form-item>
                </div>

                <!-- Phone -->
                <el-form-item
                    :label="$t.__('common.labels.phone')"
                    prop="phone_number"
                    :error="errors.get('phone_number')"
                >
                    <CompassTelInput
                        :modelValue="form.phone_number"
                        :error="!isValidPhone"
                        :defaultCountry="form.phone_country.toLowerCase()"
                        :size="componentSize"
                        data-bot="quicktrial__phonenumber"
                        @update:modelValue="updatePhoneNumber"
                        @validate="onValidateTelInput"
                        @blur="onBlurTelInput"
                        @country-changed="updatePhoneCountry"
                    />
                </el-form-item>

                <!-- Email -->
                <el-form-item
                    :label="$t.__('common.labels.professional_email')"
                    prop="login"
                    :error="errors.get('login')"
                >
                    <CompassInput v-model="form.login" :size="componentSize" data-bot="quicktrial__email" type="email" />
                </el-form-item>

                <!-- Password -->
                <el-form-item
                    :label="$t.__('common.labels.password')"
                    prop="password"
                    :error="errors.get('password')"
                    class="onboarding-quicktrial__signup-form-item--password"
                >
                    <PasswordWithBadgeInput v-model="form.password" :size="componentSize" data-bot="quicktrial__password" />
                </el-form-item>

                <!-- Mailing -->
                <el-form-item>
                    <CompassCheckbox
                        modelValue="mailing"
                        :label="$t.__('pages.onboarding.trial.OnboardingQuicktrial.mailing_label')"
                        :checked="form.mailing"
                        @change="
                            () => {
                                form.mailing = !form.mailing;
                            }
                        "
                    />
                </el-form-item>

                <p
                    class="onboarding-quicktrial__signup-form-terms typographies-paragraph-medium-regular"
                    v-html="termsLabelLegalInformation"
                />

                <CompassButton
                    :label="$t.__('common.actions.create_account')"
                    type="primary"
                    :size="componentSize"
                    class="onboarding-quicktrial__signup-form-item"
                    :loading="loading"
                    @click.prevent="submitForm('form', 'signup')"
                />
            </el-form>

            <p
                :class="[
                    'onboarding-quicktrial__signup-signin',
                    isDesktop
                        ? 'typographies-paragraph-large-semibold'
                        : 'typographies-paragraph-medium-semibold',
                ]"
            >
                {{ $t.__('pages.onboarding.trial.OnboardingQuicktrial.already_acount') }}
                <a :href="urlSipHost" class="onboarding-quicktrial__signup-signin--link">
                    {{ $t.__('common.actions.login') }}
                </a>
            </p>

            <p
                class="onboarding-quicktrial__signup-terms typographies-paragraph-small-regular"
                v-html="legalsTerms"
            />
        </div>
    </div>
</template>

<script>
import FormMixins from 'mixins/FormMixins';
import QuickTrialMixins from 'mixins/QuicktrialMixins';
import PasswordWithBadgeInput from 'vcomponents/inputs/PasswordWithBadgeInput.vue';
import {
    checkNotBlank,
    checkedNotSellsyMail,
    checkIsValidPassword,
    checkContainNoNumber,
} from 'vcomponents/forms/rules/Rules';
import { isDesktop } from 'utils/global';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

export default {
    components: {
        SaasIcon,
        PasswordWithBadgeInput,
    },
    mixins: [FormMixins, QuickTrialMixins],
    data() {
        return {
            isDesktop: isDesktop(),
            countries: window.App.countries.data || [],
            form: {
                name: '',
                lastname: '',
                firstname: '',
                phone_country: 'fr',
                phone_number: '',
                login: '',
                password: '',
                mailing: false,
                terms: true,
                country: 'FR',
                lang: 'fr',
                'g-recaptcha': null,
                utm_source: undefined,
                utm_medium: undefined,
                utm_campaign: undefined,
                google: null,
            },
            isValidPhone: true,
            loading: false,
            rules: {
                login: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_an_email_address'),
                        trigger: 'blur',
                    },
                    {
                        type: 'email',
                        message: this.$t.__('common.labels.please_enter_a_valid_email_address'),
                        trigger: 'blur',
                    },
                    {
                        validator: checkedNotSellsyMail,
                        message: this.$t.__('common.labels.please_enter_a_valid_email_address'),
                        trigger: 'blur',
                    },
                ],
                password: [
                    {
                        validator: checkIsValidPassword,
                        message: this.$t.__('common.labels.please_enter_a_password'),
                        trigger: 'blur',
                    },
                ],
                name: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_company_name'),
                        trigger: ['blur', 'change'],
                    },
                ],
                firstname: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_first_name'),
                        trigger: ['blur', 'change'],
                    },
                    {
                        validator: checkContainNoNumber,
                        message: this.$t.__(
                            'common.labels.the_firstname_must_not_include_a_number',
                        ),
                        trigger: ['blur', 'change'],
                    },
                ],
                lastname: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_name'),
                        trigger: ['blur', 'change'],
                    },
                    {
                        validator: checkContainNoNumber,
                        message: this.$t.__('common.labels.the_name_must_not_include_a_number'),
                        trigger: ['blur', 'change'],
                    },
                ],
                phone_number: [
                    {
                        validator: checkNotBlank,
                        message: this.$t.__('common.labels.please_enter_a_valid_phone_number'),
                        trigger: ['blur', 'change'],
                    },
                ],
            },
            urlSipHost: window.sipHost,
        };
    },
    computed: {
        componentSize() {
            return this.isDesktop ? 'large' : 'medium';
        },
        iconSize() {
            return this.isDesktop ? 24 : 20;
        },
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.isDesktop = isDesktop();
        });
    },
    methods: {
        signUpGoogle() {
            window.location.href = '/signup/google?quicktrial';
        },
        updatePhoneNumber(phoneNumber) {
            this.form.phone_number = phoneNumber;
        },
        updatePhoneCountry(country) {
            this.form.phone_country = country.iso2;
        },
    },
};
</script>

<style lang="scss" scoped>
.onboarding-quicktrial {
    &__signup {
        max-width: 520px;
        margin: 0 auto;

        &-title {
            text-align: center;
            margin: 0 auto var(--spacing-5);

            @media screen and (max-width: 1023px) {
                text-align: left;
            }
        }

        &-google {
            &-button {
                width: 100%;

                :deep() {
                    &.compass-button--large .compass-button__content {
                        gap: var(--spacing-2);
                        padding: var(--spacing-1) var(--spacing-3);
                    }

                    &.compass-button--medium .compass-button__content {
                        gap: 10px;
                        padding: 6px var(--spacing-3);
                    }
                }
            }
        }

        &-divider {
            text-align: center;
            margin: var(--spacing-3) 0;
            position: relative;

            &:before {
                position: absolute;
                content: '';
                height: 1px;
                background-color: var(--colors-common-border-lighter);
                left: 0;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
                z-index: 0;
            }

            &-text {
                color: var(--colors-common-text-standard-caption);
                padding: 0 var(--spacing-3);
                margin: 0;
                position: relative;
                background-color: var(--colors-common-background-default);
            }
        }

        :deep() {
            .compass-form-item {
                margin: 0 0 var(--spacing-3);
            }
        }

        &-form {
            &-items-horizontal {
                display: flex;
                align-items: flex-start;
                gap: var(--spacing-3);

                @media screen and (max-width: 1023px) {
                    flex-direction: column;
                    gap: 0;
                }
            }

            &-item {
                width: 100%;

                &--password {
                    margin-bottom: var(--spacing-5);
                }
            }

            &-terms {
                margin: var(--spacing-3) 0;
            }

            :deep() {
                .el-form-item__label {
                    font-weight: var(--font-weights-lato-1);
                    font-size: var(--font-size-17);
                    line-height: var(--line-heights-24);

                    @media screen and (max-width: 1023px) {
                        font: var(--font-families-lato);
                        font-weight: var(--font-weights-lato-1);
                        font-size: var(--font-size-14);
                        line-height: var(--line-heights-20);
                    }
                }

                .el-form-item__error {
                    line-height: var(--font-size-20);
                }

                .el-form-item__content {
                    .compass-input,
                    .compass-select,
                    .compass-tel-input,
                    .sellsy-password-input {
                        width: 100%;
                    }
                }
            }
        }

        &-signin {
            text-align: center;
            color: var(--colors-common-text-standard-caption);
            margin: 0 0 var(--spacing-4);

            &--link {
                color: var(--colors-common-text-primary-default);
                margin-left: var(--spacing-1);

                &:hover {
                    color: var(--colors-common-text-primary-hover);
                }
            }
        }

        &-terms {
            color: var(--colors-common-text-standard-caption);
            margin-top: var(--spacing-6);
        }
    }
}
</style>
